// Loan Compliance Module v1.0 PR 3 — display labels + per-module metadata
// for the Legal Ops tab. Module-code strings ("1.1-Identity" / ...) come
// from PhoenixCapital.Core.Enums.Compliance.LegalOpsModuleCodes.

import type { LegalOpsModule } from "@/api/compliance/types";
import type { LoanDetailTabId } from "@/pages/portfolioAdmin/LoanDetailPage";

export interface LegalOpsModuleMeta {
  module: LegalOpsModule;
  code: string;
  title: string;
  /** Default cadence string the recorder pre-fills. */
  defaultCadence: string;
  /**
   * Loan-detail tab the user can deep-link into for the underlying
   * operational record. Null for Identity (no dedicated tab yet).
   */
  linkedTab: LoanDetailTabId | null;
  linkedTabLabel: string | null;
}

export const LEGAL_OPS_MODULES: readonly LegalOpsModuleMeta[] = [
  {
    module: "LoanIdentity",
    code: "1.1-Identity",
    title: "1.1 Loan Identity",
    defaultCadence: "Annual",
    linkedTab: null,
    linkedTabLabel: null,
  },
  {
    module: "UccFilingMonitoring",
    code: "1.2-UCC",
    title: "1.2 UCC Filing Monitoring",
    defaultCadence: "Quarterly",
    linkedTab: "ucc",
    linkedTabLabel: "UCC tab",
  },
  {
    module: "InsuranceRenewal",
    code: "1.3-Insurance",
    title: "1.3 Insurance Renewal",
    defaultCadence: "Annual",
    linkedTab: "insurance",
    linkedTabLabel: "Insurance tab",
  },
  {
    // The Due Dates tab retired — DACA cadence items now surface on the
    // Overview needs-attention feed.
    module: "DacaMonitoring",
    code: "1.4-DACA",
    title: "1.4 DACA Monitoring",
    defaultCadence: "Monthly",
    linkedTab: "overview",
    linkedTabLabel: "Overview feed",
  },
] as const;

export function metaForModule(module: LegalOpsModule): LegalOpsModuleMeta {
  return LEGAL_OPS_MODULES.find((m) => m.module === module)!;
}

export const FAILURE_REASON_LABELS: Record<string, string> = {
  InsuranceLapse: "Insurance lapse",
  KeyManInsuranceLapse: "Key-man insurance lapse",
  UccContinuationOverdue: "UCC continuation overdue",
  UccPerfectionDefect: "UCC perfection defect",
  DacaInactive: "DACA inactive",
  MissingGoodStanding: "Missing good standing",
  Other: "Other",
};

export const MATERIAL_EVENT_TYPE_LABELS: Record<string, string> = {
  Litigation: "Litigation",
  MaterialAdverseChange: "Material adverse change",
  KeyOfficerDeparture: "Key officer departure",
  ChangeOfControl: "Change of control",
  CybersecurityIncident: "Cybersecurity incident",
  GovernmentInvestigation: "Government investigation",
  Judgment: "Judgment",
  MaterialCustomerLoss: "Material customer loss",
  NameOrStructureChange: "Name or structure change",
  EntityStatusChange: "Entity status change",
  Other: "Other",
};

export const MATERIAL_EVENT_SOURCE_LABELS: Record<string, string> = {
  BorrowerDisclosure: "Borrower disclosure",
  ServicerRecord: "Servicer record",
  ComplianceDetected: "Compliance-detected",
  LoanEventLinked: "Linked loan event",
  ThirdPartyNotice: "Third-party notice",
  Other: "Other",
};

export const MATERIAL_EVENT_DISPOSITION_LABELS: Record<string, string> = {
  Open: "Open",
  UnderReview: "Under review",
  NoticeRequired: "Notice required",
  NoticeSent: "Notice sent",
  Resolved: "Resolved",
  EscalatedToTrack2: "Escalated → Track 2",
  EscalatedToTrack3: "Escalated → Track 3",
};
